import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Car, Filter, Eye, FileDown, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { PageHeader } from "@/components/PageHeader"
import { useIsMobile } from "@/hooks/use-mobile"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { criteriosData } from "@/lib/criteriosData"
import { pdf } from "@react-pdf/renderer"
import PropostaPDF from "@/components/PropostaPDF"

interface PropostaCompleta {
  id: string
  avaliacao_id: string
  valor_fipe: number
  custo_reparos: number
  valor_liquido_sugerido: number
  margem_lucro: number
  valor_ofertado: number
  modalidade?: 'compra' | 'consignacao' | string
  observacoes: string | null
  status: string
  created_at: string
  updated_at: string
  avaliacoes?: {
    id: string
    veiculo_id: string
    status: string
    nota_final?: number
    created_at: string
    veiculos?: {
      id: string
      placa: string
      marca: string
      modelo: string
      ano: number
      cor?: string
      quilometragem?: number
      foto_principal?: string
    }
  }
}

interface AvaliacaoCriterioPDF {
  criterio_id: string
  aprovado: boolean | null
  status?: string | null
  observacoes: string | null
  custo_reparo: number | null
}

const categoriaPorPrefixo: Record<string, string> = {
  frontal: "Frontal",
  traseira: "Traseira",
  latdir: "Lateral Direita",
  latesq: "Lateral Esquerda",
  motor: "Motor",
  painel: "Painel e Instrumentos",
  interior: "Interior",
  extras: "Detalhes Extras",
}

const normalizarCriterioId = (id: string) => id.trim().toLowerCase()

const obterCategoria = (criterioId: string) => {
  const id = normalizarCriterioId(criterioId)
  const criterioLocal = criteriosData.find((criterio) => criterio.id === id)
  if (criterioLocal?.categoria) return criterioLocal.categoria
  return categoriaPorPrefixo[id.split("_")[0]] || ""
}

const obterNome = (criterioId: string) => {
  const id = normalizarCriterioId(criterioId)
  return criteriosData.find((criterio) => criterio.id === id)?.nome || criterioId
}

const isCriterioReprovado = (criterio: AvaliacaoCriterioPDF) =>
  criterio.status ? criterio.status === "reprovado" : criterio.aprovado === false

const PropostasConcluidas = () => {
  const vertical = useTipoOperacao()
  const [searchTerm, setSearchTerm] = useState("")
  const [propostas, setPropostas] = useState<PropostaCompleta[]>([])
  const [loading, setLoading] = useState(true)
  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    fetchPropostas()
  }, [])

  const fetchPropostas = async () => {
    try {
      const { data: propostasData, error: propostasError } = await supabase
        .from('propostas')
        .select(`
          *,
          avaliacoes (
            id,
            veiculo_id,
            status,
            nota_final,
            created_at,
            veiculos (
              id,
              placa,
              marca,
              modelo,
              ano,
              cor,
              quilometragem,
              foto_principal
            )
          )
        `)
        .order('created_at', { ascending: false })

      if (propostasError) {
        toast.error("Erro ao carregar fechamentos")
        return
      }

      const propostasResolvidas = await Promise.all(
        (propostasData || []).map(async (proposta) => {
          if (!proposta.avaliacoes?.veiculos) {
            return {
              ...proposta,
              avaliacoes: proposta.avaliacoes ?? undefined,
            } as PropostaCompleta
          }

          return {
            ...proposta,
            avaliacoes: {
              ...proposta.avaliacoes,
              veiculos: {
                ...proposta.avaliacoes.veiculos,
                foto_principal:
                  (await createSignedFileUrl(PHOTO_BUCKET, proposta.avaliacoes.veiculos.foto_principal)) ??
                  proposta.avaliacoes.veiculos.foto_principal,
              },
            },
          } as PropostaCompleta
        })
      )

      setPropostas(propostasResolvidas)
    } catch (error) {
      console.error("Erro ao carregar fechamentos:", error)
      toast.error("Erro inesperado ao carregar dados")
    } finally {
      setLoading(false)
    }
  }

  const propostasValidas = propostas.filter(
    (proposta): proposta is PropostaCompleta & { avaliacoes: NonNullable<PropostaCompleta["avaliacoes"]> & { veiculos: NonNullable<NonNullable<PropostaCompleta["avaliacoes"]>["veiculos"]> } } =>
      Boolean(proposta.avaliacoes?.veiculos)
  )

  const filteredPropostas = propostasValidas.filter((proposta) =>
    proposta.avaliacoes.veiculos.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proposta.avaliacoes.veiculos.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proposta.avaliacoes.veiculos.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proposta.avaliacoes.veiculos.ano.toString().includes(searchTerm)
  )

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const formatRelativeTone = (proposta: PropostaCompleta) => {
    if (!proposta.valor_fipe || !proposta.valor_ofertado) return null
    const delta = ((proposta.valor_ofertado - proposta.valor_fipe) / proposta.valor_fipe) * 100
    if (Math.abs(delta) < 0.05) return "Na FIPE"
    return `${delta > 0 ? "+" : ""}${delta.toFixed(1)}% da FIPE`
  }

  const proposalCards = [
    {
      label: "Base salva",
      value: propostasValidas.length,
      note: "Fechamentos registrados",
    },
    {
      label: "Volume ofertado",
      value: formatCurrency(propostasValidas.reduce((acc, proposta) => acc + (proposta.valor_ofertado || 0), 0)),
      note: "Soma das ofertas geradas",
    },
    {
      label: "Margem média",
      value: propostasValidas.length > 0
        ? `${(propostasValidas.reduce((acc, proposta) => acc + (proposta.margem_lucro || 0), 0) / propostasValidas.length).toFixed(1)}%`
        : "0%",
      note: "Média da operação",
    },
    {
      label: "Na visão atual",
      value: filteredPropostas.length,
      note: "Resultado da busca aplicada",
    },
  ]

  const handleGerarPDF = async (proposta: PropostaCompleta) => {
    setGeneratingPdfId(proposta.id)

    try {
      const { data: criteriosDataBanco, error: criteriosError } = await supabase
        .from("avaliacoes_criterios")
        .select("criterio_id, aprovado, status, observacoes, custo_reparo")
        .eq("avaliacao_id", proposta.avaliacao_id)

      if (criteriosError) throw criteriosError

      const criteriosReprovados = (criteriosDataBanco || []).filter((criterio) => isCriterioReprovado(criterio))

      const { data: fotosReparosData, error: fotosError } = await supabase
        .from("fotos_veiculo")
        .select("categoria, url")
        .eq("avaliacao_id", proposta.avaliacao_id)
        .like("categoria", "criterio_reprovado_%")

      if (fotosError) throw fotosError

      const fotosReparosResolvidas = await Promise.all(
        (fotosReparosData || []).map(async (foto) => ({
          ...foto,
          url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
        }))
      )

      const reparosParaPDF = criteriosReprovados
        .sort((left, right) => (right.custo_reparo || 0) - (left.custo_reparo || 0))
        .map((criterio) => ({
          nome: obterNome(criterio.criterio_id),
          categoria: obterCategoria(criterio.criterio_id),
          custo: criterio.custo_reparo || 0,
          observacoes: criterio.observacoes || undefined,
          fotoUrl: fotosReparosResolvidas.find(
            (foto) => foto.categoria === `criterio_reprovado_${criterio.criterio_id}`
          )?.url,
        }))

      const blob = await pdf(
        <PropostaPDF
          modoOficina={!vertical.documentos.mostrarValoresFinanceiros}
          placa={proposta.avaliacoes.veiculos.placa}
          marca={proposta.avaliacoes.veiculos.marca}
          modelo={proposta.avaliacoes.veiculos.modelo}
          ano={proposta.avaliacoes.veiculos.ano}
          cor={proposta.avaliacoes.veiculos.cor}
          quilometragem={proposta.avaliacoes.veiculos.quilometragem}
          fotoPrincipalUrl={proposta.avaliacoes.veiculos.foto_principal}
          notaFinal={proposta.avaliacoes.nota_final}
          valorFIPE={proposta.valor_fipe}
          custoReparos={proposta.custo_reparos}
          valorLiquido={proposta.valor_liquido_sugerido}
          margemLucro={proposta.margem_lucro}
          valorOfertado={proposta.valor_ofertado}
          reparos={reparosParaPDF}
          observacoes={proposta.observacoes || undefined}
        />
      ).toBlob()

      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      const slug = vertical.documentos.mostrarValoresFinanceiros ? 'fechamento' : 'termo-entrada'
      link.download = `${slug}-${proposta.avaliacoes.veiculos.placa || proposta.id}.pdf`
      link.click()
      URL.revokeObjectURL(url)
      toast.success("PDF gerado com sucesso")
    } catch (error) {
      console.error("Erro ao gerar PDF do fechamento:", error)
      toast.error("Erro ao gerar PDF")
    } finally {
      setGeneratingPdfId(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={vertical.documentos.tituloListagem}
        description={vertical.documentos.descricaoListagem}
      >
        <Button variant="outline" asChild>
          <Link to="/avaliacoes">
            <Car className="mr-2 h-4 w-4" />
            {vertical.labels.criarDocumento}
          </Link>
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-4">
        {proposalCards.map((item) => (
          <Card key={item.label}>
            <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">{item.label}</p>
              <p className="font-data mt-2 text-[2rem] font-semibold leading-none text-foreground sm:mt-3 sm:text-3xl">{item.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3.5 sm:p-4 md:p-5 md:pt-5">
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-muted-foreground sm:top-3.5" />
            <Input
              placeholder="Buscar por placa, modelo ou ano..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 pl-10 sm:h-11"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de Fechamentos */}
      <Card>
        <CardHeader>
          <CardTitle>Fechamentos</CardTitle>
          <CardDescription>
            {filteredPropostas.length} fechamento{filteredPropostas.length !== 1 ? 's' : ''} encontrado{filteredPropostas.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPropostas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-6 mb-4">
                <FileText className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm ? "Nenhum fechamento encontrado" : "Nenhum fechamento salvo"}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                {searchTerm
                  ? "Tente ajustar os termos de busca"
                  : propostas.length > 0
                    ? "Os fechamentos ligados a veículos removidos foram ocultados para evitar inconsistências."
                    : "Crie alguns fechamentos para começar a gerenciá-los"
                }
              </p>
              <Button asChild size={isMobile ? "sm" : "default"}>
                <Link to="/avaliacoes">
                  <Car className="mr-2 h-4 w-4" />
                  Criar Fechamento
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPropostas.map((proposta) => (
                <Card key={proposta.id} className="group overflow-hidden border-border/70 bg-gradient-to-r from-background to-muted/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="relative aspect-[4/3] overflow-hidden bg-muted md:w-56 md:aspect-square">
                        {proposta.avaliacoes.veiculos.foto_principal ? (
                          <img
                            src={proposta.avaliacoes.veiculos.foto_principal}
                            alt={`Foto do veículo ${proposta.avaliacoes.veiculos.placa}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <Car className="h-12 w-12 text-muted-foreground" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4">
                          <p className="text-lg font-semibold text-white">{proposta.avaliacoes.veiculos.placa}</p>
                          <p className="text-sm text-white/80">
                            {proposta.avaliacoes.veiculos.marca} {proposta.avaliacoes.veiculos.modelo}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4 p-5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-semibold text-2xl tracking-tight">{proposta.avaliacoes.veiculos.placa}</h3>
                              <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs">
                                {proposta.avaliacoes.veiculos.ano}
                              </Badge>
                            </div>
                            <p className="mt-1 text-muted-foreground text-base">
                              {proposta.avaliacoes.veiculos.marca} {proposta.avaliacoes.veiculos.modelo}
                              {proposta.avaliacoes.veiculos.cor && ` • ${proposta.avaliacoes.veiculos.cor}`}
                            </p>
                          </div>
                          <div className="text-left md:text-right">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                              Oferta final
                            </p>
                            <p className="text-2xl font-bold text-primary">{formatCurrency(proposta.valor_ofertado)}</p>
                            {formatRelativeTone(proposta) && (
                              <p className="text-sm text-muted-foreground">{formatRelativeTone(proposta)}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm xl:grid-cols-4">
                          <div className="rounded-xl border bg-background/80 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">FIPE</p>
                            <p className="mt-2 font-semibold text-blue-600">{formatCurrency(proposta.valor_fipe)}</p>
                          </div>
                          <div className="rounded-xl border bg-background/80 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Reparos</p>
                            <p className="mt-2 font-semibold text-red-600">{formatCurrency(proposta.custo_reparos)}</p>
                          </div>
                          <div className="rounded-xl border bg-background/80 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Valor base</p>
                            <p className="mt-2 font-semibold text-green-600">{formatCurrency(proposta.valor_liquido_sugerido)}</p>
                          </div>
                          <div className="rounded-xl border bg-background/80 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Margem</p>
                            <p className="mt-2 font-semibold text-foreground">{proposta.margem_lucro}%</p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 rounded-xl border border-dashed bg-muted/20 px-4 py-3 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span>Oferta criada em {formatDate(proposta.created_at)}</span>
                            {proposta.avaliacoes.nota_final && (
                              <span>Nota da avaliação: {proposta.avaliacoes.nota_final.toFixed(1)}/10</span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2">
                          <Button size="sm" className="w-full justify-start sm:justify-center" asChild>
                            <Link to={`/proposta/${proposta.avaliacao_id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalhes
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start sm:justify-center"
                            onClick={() => handleGerarPDF(proposta)}
                            disabled={generatingPdfId === proposta.id}
                          >
                            {generatingPdfId === proposta.id ? (
                              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                            ) : (
                              <FileDown className="h-4 w-4 mr-2" />
                            )}
                            {generatingPdfId === proposta.id ? "Gerando PDF..." : "Gerar PDF"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default PropostasConcluidas
